import { ReactComponent as ETRActivityLogIcon } from "@/assets/svgs/icon-activity-log-etr.svg";
import { ReactComponent as ArrowUpCircle } from "@/assets/svgs/arrow-up-circle.svg";
import { ReactComponent as XCircleRed } from "@/assets/svgs/x-circle-red.svg";
import { ReactComponent as ETRRequestedFailed } from "@/assets/svgs/issuance-failed-icon.svg";
import { ReactComponent as ActivityLogIconVerified } from "@/assets/svgs/etr-activity-log-issuance-issued.svg";
import { ReactComponent as ActivityLogIconIssued } from "@/assets/svgs/etr-activity-log-issuance-created.svg";
import { ReactComponent as CheckDouble } from "@/assets/svgs/check-double.svg";
import { ReactComponent as CheckListIcon } from "@/assets/svgs/view-activity-log-list-icon.svg";
import rem from "@/utils/rem.js";
import Button from "@/components/Button";
import PropTypes from "prop-types";
import moment from "moment";
import { differenceWith } from "lodash";
import useStaticData from "@/hooks/use-static-data/index";
import useDid from "@/hooks/use-did/index";
import { ReactComponent as IconNext } from "@/assets/svgs/icon-down.svg";
import useEtr from '@/hooks/use-etr/index';
import { useState, useEffect } from "react";
import useConfig from '@/hooks/use-config/index';
import CardConfigure from '@/componentsDS/Modal/CardConfigure'
import Modal from '@/componentsDS/Modal/Modal'
import { feature } from "@/utils/feature-verifier.js";
import { featureConstant } from "@/constants/configuration/feature.js";
import { useFeature } from "@/hooks/use-feature/index";
import { renderUserEmailInline } from '@/utils/formatUserEmail';

function ActivityLogAccordion({ added = {}, removed = {}, getOrgName, transferType }) {
	const [isOpen, setIsOpen] = useState(true);
	const isNonChangeableType = ['ENDORSE_OWNER', 'SURRENDER'].includes(transferType);

	const isETRIGPNITRV5FeatureEnabled = feature(featureConstant?.ETR_IGPNI_TR_V5);

	function renderStatusListRow({ icon = <CheckListIcon />, label, value }) {
		if (!value) return null;
		return (
			<div className="view-request-log-status-list">
				{icon}
				<span className="view-request-log-status-text">
					{label} <br />
					{value}
				</span>
			</div>
		);
	};

	const renderSection = (title, data) => {
		let ownerDisplay = null;
		let holderToDisplay = null;
		const { owner, holder, remarks } = data || {};
		if (!owner && !holder && !remarks) return null;

		if (owner) {
			const ownerName = getOrgName ? getOrgName(owner) : owner;
			ownerDisplay = `${ownerName} (${owner})`;
		}
		if (holder) {
			const holderName = getOrgName ? getOrgName(holder) : holder;
			holderToDisplay = `${holderName} (${holder})`;
		}
		return (
			<div className="accordion-section">
				<div className="view-request-log-status-list">
					<CheckListIcon />
					<span className="view-request-log-status-text">{title}</span>
				</div>
				<div className="accordion-section-title"></div>
				<div className="accordion-section-content" style={{ paddingLeft: rem(20) }} >
					{renderStatusListRow({
						label: "Assign ownership to:",
						value: ownerDisplay
					})}
					{renderStatusListRow({
						label: "Assign holdership to:",
						value: holderToDisplay
					})}
					{!isETRIGPNITRV5FeatureEnabled && isNonChangeableType
						? <span className="view-request-log-status-text">No Changes <br /></span>
						: (owner || holder) && renderStatusListRow({
							label: "Share data to:",
							value: <>
								{owner && <span>{getOrgName(owner)} <br /></span>}
								{holder && <span>{getOrgName(holder)} <br /></span>}
								{!owner && !holder && <span>No Changes <br /></span>}
							</>
						})
					}
					{
						(owner || holder) && renderStatusListRow({
							label: "Share data to:",
							value: <>
								{owner && <span>{getOrgName(owner)} <br /></span>}
								{holder && <span>{getOrgName(holder)} <br /></span>}
								{!owner && !holder && <span>No Changes <br /></span>}
							</>
						})

					}
					{renderStatusListRow({
						label: "Remarks:",
						value: remarks
					})}
				</div>
			</div>
		);
	};

	return (
		<div className="request-log__updated-block">
			<Button
				nohover
				nobackground
				addClass={`request-log__updated--header-section ${isOpen ? "expanded" : ""}`}
				submit={() => setIsOpen((open) => !open)}
				prefix={
					<div className="request-log__updated--header-section-left">
						Changes Made
					</div>
				}
				suffix={
					<div className="request-log__updated--header-section-right">
						<div>{isOpen ? "Less Details" : "More Details"}</div>
						<IconNext />
					</div>
				}
			/>
			{isOpen && (
				<div className="accordion-body">
					{renderSection("Added", added)}
					{renderSection("Removed", removed)}
				</div>
			)}
		</div>
	);
};

ActivityLogAccordion.propTypes = {
	added: PropTypes.shape({
		owner: PropTypes.string,
		holder: PropTypes.string,
		remarks: PropTypes.string,
	}),
	removed: PropTypes.shape({
		owner: PropTypes.string,
		holder: PropTypes.string,
		remarks: PropTypes.string,
	}),
	getOrgName: PropTypes.func,
	transferType: PropTypes.string,
};

function commonSchemaOperations() {
	const getType = (typeArr) => Array.isArray(typeArr) ? typeArr.find(ct => ct !== null) : typeArr;

	const findNestedKey = (schema, key) => {
		if (schema[key]) return schema[key];
		for (let prop in schema) {
			if (schema.hasOwnProperty(prop)) {
				let result;
				if (schema[prop]?.properties) {
					result = findNestedKey(schema[prop].properties, key);
				} else if (schema[prop]?.items?.properties) {
					result = findNestedKey(schema[prop].items.properties, key);
				} else {
					result = null;
				}
				if (result) return result;
			}
		}
		return null;
	};

	const searchSchema = (schema, key) => {
		if (key.includes('[')) {
			const baseKey = key.split('[')[0];
			return getType(schema[baseKey]?.type) === 'array' ? schema[baseKey].items.properties : null;
		}
		return findNestedKey(schema, key);
	};

	const findSchemaPart = (schema, keys) => keys.reduce((acc, key) => acc ? searchSchema(acc, key) : null, schema);

	return { getType, findNestedKey, searchSchema, findSchemaPart };
}

export function getFieldTitle(key, schema) {
	const { findSchemaPart } = commonSchemaOperations();

	let keyToSearch = key.includes('.') ? key.split('.').pop() : key;
	let foundSchema = findSchemaPart(schema, [keyToSearch]);
	if (foundSchema?.properties || foundSchema?.items?.properties) return null;

	return foundSchema?.title || null;
}

export function getFieldPath(rawPath, schema) {
	const { findSchemaPart } = commonSchemaOperations();

	const processKey = (key, schema) => {
		if (!key) return null;
		const [baseKey, index] = key.includes('[') ? key.split('[') : [key];
		const itemIndex = index ? parseInt(index.replace(']', ''), 10) + 1 : null;
		const baseTitle = findSchemaPart(schema, [baseKey])?.title;
		return itemIndex ? `${baseTitle} # ${itemIndex}` : baseTitle || null;
	};

	const getFilePath = (pathArray, schema) => pathArray.reduce((acc, key) => {
		const title = processKey(key, schema);
		return title ? [...acc, title] : acc;
	}, []).join(' / ') || null;

	const rawPathArray = rawPath.split('.');
	if (rawPathArray.length <= 1) return null;

	const acceptanceKey = rawPathArray.pop();
	const acceptanceSchema = findSchemaPart(schema, [acceptanceKey]);
	if (!acceptanceSchema || acceptanceSchema?.properties || acceptanceSchema?.items?.properties) return null;

	return getFilePath(rawPathArray, schema);
}

const ViewActivityLogModal = ({ visible, close, schema, activityLog, activityLogType = "", isApprovalScreen = false, etrDocDetails = {} }) => {
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const staticData = useStaticData();
	const { toFindOrgName } = useDid();
	const dataElementId = "ebl"
	const { identity, provideItems, consumeItems } = useConfig()
	const { fetchEtr, etrData } = useEtr({
		dataElementId,
		configData: {
			consumeItems,
			provideItems,
			identity
		}
	})
	const [isOwnerDetailsVisible, setIsOwnerDetailsVisible] = useState(true);
	const [isHolderDetailsVisible, setIsHolderDetailsVisible] = useState(true);
	const [isDataSharingVisible, setIsDataSharingVisible] = useState(true);
	const isETRIGPNITRV5FeatureEnabled = feature(featureConstant?.ETR_IGPNI_TR_V5);

	useEffect(() => {
		if ((provideItems || consumeItems) && identity) {
			fetchEtr()
		}
	}, [provideItems, identity, consumeItems])

	function getOrgName(walletAddress) {
		const orgName = toFindOrgName(walletAddress)
		if (orgName) {
			return orgName
		}

		if (etrData.some(item => item.walletAddress === walletAddress)) {
			return etrData.find((item) => item.walletAddress === walletAddress).name
		}

		return null
	}

	function renderApproved(email, created) {
		return (
			<>
				<div className="view-request-log-icon"><CheckDouble /></div>
				<div className="view-request-log-heading">
					Request Approved<br />
					<span className="view-request-log-text">by {email} on {moment(created).format("DD/MM/YYYY h:mma")}</span>
				</div>
			</>
		);
	}

	function renderUpdated(email, created, changedFields, currentPayload, prevPayload) {
		const renderFields = renderChangedFields(changedFields);
		const renderUpdatedAttachments = renderAttachments(currentPayload?.attachments, prevPayload?.attachments);

		return (
			<>
				<div className="view-request-log-icon"><ArrowUpCircle /></div>
				<div className="view-request-log-heading">
					Request Updated<br />
					{renderFields}
					{renderUpdatedAttachments}
					<span className="view-request-log-text">by {email} on {moment(created).format("DD/MM/YYYY h:mma")}</span>
				</div>
			</>
		);
	}

	function renderSubmitted(email, created) {
		return (
			<>
				<div className="view-request-log-icon"><ArrowUpCircle /></div>
				<div className="view-request-log-heading">
					Request Submitted<br />
					<span className="view-request-log-text">by {email} on {moment(created).format("DD/MM/YYYY h:mma")}</span>
				</div>
			</>
		);
	}

	function renderRejected(email, created, reason) {
		return (
			<>
				<div className="view-request-log-icon"><XCircleRed /></div>
				<div className="view-request-log-heading">Request Rejected<br />
					<span className="view-request-log-status-text">Rejected Reason: {reason}</span><br />
					<span className="view-request-log-text">by {email} on {moment(created).format("DD/MM/YYYY h:mma")}</span>
				</div>
			</>
		);
	}

	function getStatus(itemStatus, reason, changedFields, currentPayload, prevPayload, email, created) {
		switch (itemStatus) {
			case "APPROVED":
				return renderApproved(email, created);
			case "SUBMITTED":
				return renderSubmitted(email, created);
			case "REJECTED":
				return renderRejected(email, created, reason);
			case "UPDATED":
				return renderUpdated(email, created, changedFields, currentPayload, prevPayload);
			default:
				return null;
		}
	}

	const renderBody = activityLog?.map((item, index) => {
		const prevPayload = findPrevPayload(activityLog, index);
		const reason = item.transactionRequestLog?.reason;
		const changedFields = item.transactionRequestLog?.messageStoreChangeHistory?.changedFields;
		const currentPayload = item.transactionRequestLog?.messageStoreChangeHistory?.changedPayload;
		const updatedEmail = isSoftDeleteUserEnabled ? renderUserEmailInline(item.email) : item.email;
		return (
			<div className="view-request-log" key={index}>
				{getStatus(item.action, reason, changedFields, currentPayload, prevPayload, updatedEmail, item.created)}
			</div>
		)
	});

	function findPrevPayload(activityLog, index) {
		const allowedActions = ["SUBMITTED", "UPDATED"];
		for (let i = index + 1; i < activityLog.length; i++) {
			const prevItem = activityLog[i];
			if ((allowedActions.includes(prevItem?.action)) && prevItem.transactionRequestLog?.messageStoreChangeHistory?.changedPayload) {
				return prevItem.transactionRequestLog.messageStoreChangeHistory.changedPayload;
			}
		}
		return {};
	}

	function renderChangedFields(changedFields) {
		const renderedPaths = new Set();

		return changedFields?.map((field) => {
			if (!field.includes('attachments')) {
				let formattedPropertyName;
				let formattedPropertyPath;
				if (field && schema?.properties) {
					formattedPropertyName = getFieldTitle(field, schema?.properties);
					formattedPropertyPath = getFieldPath(field, schema?.properties);
				}

				const propertyPathElement = formattedPropertyPath && !renderedPaths.has(formattedPropertyPath) ? (
					<span key={formattedPropertyPath}>{formattedPropertyPath}</span>
				) : null;

				if (formattedPropertyPath) {
					renderedPaths.add(formattedPropertyPath);
				}

				const propertyNameElement = formattedPropertyName ? (
					<span key={formattedPropertyName}>Updated E-Form Field: {formattedPropertyName}</span>
				) : null;

				return (
					<span className="view-request-log-status-text__nested" key={formattedPropertyName}>
						{propertyPathElement && <>{propertyPathElement}</>}
						{propertyNameElement && <>{propertyNameElement}</>}
					</span>
				);
			}
			return null;
		}).filter(Boolean);
	}

	function renderAttachments(currentAttachments, previousAttachments) {
		let addedAttachments = differenceWith(currentAttachments, previousAttachments, (a, b) => a.filename === b.filename && a.file_content === b.file_content);
		let removedAttachments = differenceWith(previousAttachments, currentAttachments, (a, b) => a.filename === b.filename && a.file_content === b.file_content);

		const updatedAttachments = previousAttachments?.filter((previousAttachment) => {
			const correspondingCurrentAttachment = currentAttachments.find((currentAttachment) => currentAttachment.filename === previousAttachment.filename);
			return correspondingCurrentAttachment && correspondingCurrentAttachment.file_content !== previousAttachment.file_content;
		});
		updatedAttachments?.forEach((updatedAttachment) => {
			addedAttachments = addedAttachments.filter(attachment => attachment.filename !== updatedAttachment.filename);
			removedAttachments = removedAttachments.filter(attachment => attachment.filename !== updatedAttachment.filename);
		});

		const allAttachments = [
			...addedAttachments?.map(attachment => ({ action: 'Added', attachment })) ?? [],
			...updatedAttachments?.map(attachment => ({ action: 'Updated', attachment })) ?? [],
			...removedAttachments?.map(attachment => ({ action: 'Removed', attachment })) ?? [],
		];

		return allAttachments?.map(({ action, attachment }) => (
			<span className="view-request-log-status-text" key={attachment.filename}>
				{action} Attachment: {attachment.filename}
				<br />
			</span>
		));
	}

	// ETR
	function renderedSuccessful({ etrName, icon, email, created, sharingDataTo, ETRType }) {
		return (
			<>
				<div className="view-request-log-icon">{icon}</div>
				<div className="view-request-log-heading">{ETRType} Successful<br />
					{isETRIGPNITRV5FeatureEnabled && etrName && <><span className="view-request-log-status-text">{etrName}</span><br /></>}
					{sharingDataTo && <><span className="view-request-log-status-text">Sharing Data to: {sharingDataTo}</span><br /></>}
					<span className="view-request-log-text">by {email} on {moment(created).format("DD/MM/YYYY, h:mm A")}</span>
				</div>
			</>
		);
	}

	function renderingStatus({ etrName, email, created, ETRType, remarks, showIssuer = false, type }) {
		let issuer = etrDocDetails?.issuer?.name;
		let icon = isETRIGPNITRV5FeatureEnabled ? <ArrowUpCircle /> : <ActivityLogIconIssued />;
		let isNominating = type === 'NOMINATE_OWNER';

		return (
			<>
				<div className="view-request-log-icon">{icon}</div>
				<div className="view-request-log-heading">{ETRType}<br />
					{isETRIGPNITRV5FeatureEnabled && etrName && <><span className="view-request-log-status-text">{etrName}</span><br /></>}
					{isETRIGPNITRV5FeatureEnabled && showIssuer && issuer &&
						<>
							<div className="view-request-log-status-list">
								<CheckListIcon />
								<span className="view-request-log-status-text">Return record to: <br />{issuer}</span><br />
							</div>
							<div className="view-request-log-status-list">
								<CheckListIcon />
								<span className="view-request-log-status-text">Share data to: <br />{issuer}</span><br />
							</div>
						</>
					}
					{
						isETRIGPNITRV5FeatureEnabled && !showIssuer && type === 'RESTORE' &&
						<div className="view-request-log-status-list">
							<CheckListIcon />
							<span className="view-request-log-status-text">Share data to: <br />{issuer}</span><br />
						</div>
					}
					{
						isETRIGPNITRV5FeatureEnabled && isNominating && !showIssuer &&
						<>
							<div className="view-request-log-status-list">
								<CheckListIcon />
								<span className="view-request-log-status-text">Assign Ownership to: <br />{issuer}</span><br />
							</div>
							<div className="view-request-log-status-list">
								<CheckListIcon />
								<span className="view-request-log-status-text">Share data to: <br />{issuer}</span><br />
							</div>
						</>
					}
					{isETRIGPNITRV5FeatureEnabled && etrDocDetails?.tokenRegistryVersion === "V5" && <>
						<div className="view-request-log-status-list">
							<CheckListIcon />
							<span className="view-request-log-status-text">Remarks: <br />{remarks?.trim().length > 0 ? remarks?.trim() : "-"}</span><br />
						</div></>}
					<span className="view-request-log-text">by {email} on {moment(created).format("DD/MM/YYYY, h:mm A")}</span>
				</div>
			</>
		);
	}

	function renderFailed({ etrName, email, created, sharingDataTo, ETRType, type }) {
		return (
			<>
				<div className="view-request-log-icon"><ETRRequestedFailed /></div>
				<div className="view-request-log-heading">{ETRType}<br />
					{isETRIGPNITRV5FeatureEnabled && etrName && <><span className="view-request-log-status-text">{etrName}</span><br /></>}
					{type !== 'SHRED' && sharingDataTo && <><span className="view-request-log-status-text">Sharing Data to: {sharingDataTo}</span><br /></>}
					<span className="view-request-log-text">by {email} on {moment(created).format("DD/MM/YYYY, h:mm A")}</span>
				</div>
			</>
		);
	}

	function renderIssuing(email, created, ownerAddress, holderAddress, issuedETRBy, remarks) {
		return (
			<>
				<div className="view-request-log-icon"><ActivityLogIconIssued /></div>
				<div className="view-request-log-heading">Issuance Created<br />
					<span className="view-request-log-status-text">Issued ETR: {issuedETRBy}</span><br />
					{ownerAddress && <><span className="view-request-log-status-text">Assigned ownership to: {ownerAddress}</span><br /></>}
					{holderAddress && <><span className="view-request-log-status-text">Assigned holdership to: {holderAddress}</span><br /></>}
					{isETRIGPNITRV5FeatureEnabled && etrDocDetails?.tokenRegistryVersion === "V5" && <><span className="view-request-log-status-text">Remarks: {remarks?.trim().length > 0 ? remarks?.trim() : "-"}</span><br /></>}
					<span className="view-request-log-text">by {email} on {moment(created).format("DD/MM/YYYY, h:mm A")}</span>
				</div>
			</>
		);
	}

	function renderStatusText({ showIssuer, ownerAddress, holderAddress }) {
		if (showIssuer) {
			return <span>{showIssuer} <br /></span>;
		}

		const ownerName = ownerAddress ? getOrgName(ownerAddress) : null;
		const holderName = holderAddress ? getOrgName(holderAddress) : null;

		// Both present and unique
		if (ownerName && holderName) {
			if (ownerName === holderName) {
				return <span>{ownerName} <br /></span>;
			}
			return (
				<span>
					{ownerName} <br />
					{holderName} <br />
				</span>
			);
		}

		// Only owner
		if (ownerName) {
			return <span>{ownerName} <br /></span>;
		}

		// Only holder
		if (holderName) {
			return <span>{holderName} <br /></span>;
		}

		// None present
		return <span>No Changes <br /></span>;
	}

	function renderedTransferredRequests({ icon, type, statusType, etrName, ownerAddress, holderAddress, item }) {
		let showShareData = true;
		let showIssuer = item?.transferableStore?.type === 'SURRENDER' ? item?.transferableRecord?.issuer?.name : null

		let remarks;
		let surrenderTypeToCheck = "Record Surrender";
		if (isETRIGPNITRV5FeatureEnabled) {
			let changedDetails = item?.transactionRequestLog?.transferableStoreChangeHistory?.changedDetails;
			remarks = "-";
			if (typeof changedDetails?.remarks === "string") {
				remarks = changedDetails.remarks.trim() || "-";
			} else if (typeof item?.transferableStore?.remarks === "string") {
				remarks = item.transferableStore.remarks.trim() || "-";
			}
		}
		return (
			<>
				<div className="view-request-log-icon">{icon}</div>
				<div className="view-request-log-heading">
					{type} {statusType}<br />
					{etrName && <><span className="view-request-log-status-text">{etrName}</span><br /></>}
					{ownerAddress && type.trim() !== surrenderTypeToCheck && type !== "Data Element" && <div className="view-request-log-status-list">
						<CheckListIcon />
						<span className="view-request-log-status-text">Assign ownership to: <br />{getOrgName(ownerAddress)} ({ownerAddress})</span><br />
					</div>}
					{holderAddress && type.trim() !== surrenderTypeToCheck && type !== "Data Element" && <div className="view-request-log-status-list">
						<CheckListIcon />
						<span className="view-request-log-status-text">Assign holdership to: <br />{getOrgName(holderAddress)} ({holderAddress})</span><br />
					</div>}
					{isETRIGPNITRV5FeatureEnabled && showIssuer && type.trim() === surrenderTypeToCheck && type !== "Data Element" && <div className="view-request-log-status-list">
						<CheckListIcon />
						<span className="view-request-log-status-text">Return record to: <br />{showIssuer}</span><br />
					</div>}
					{(isETRIGPNITRV5FeatureEnabled ? (showShareData) : (showShareData && !statusType?.includes("Failed"))) && <div className="view-request-log-status-list">
						<CheckListIcon />
						<span className="view-request-log-status-text">Share data to: <br />
							{renderStatusText({ showIssuer, ownerAddress, holderAddress })}
						</span><br />
					</div>}
					{isETRIGPNITRV5FeatureEnabled && etrDocDetails?.tokenRegistryVersion === "V5" && remarks && type !== "Data Element" && <div className="view-request-log-status-list">
						<CheckListIcon />
						<span className="view-request-log-status-text">Remarks: <br />{remarks}</span><br />
					</div>}
					{item.transactionRequestLog?.reason && <div className="request-log__rejection-reason--error-block">
						<span>Reason For Rejection: <br />{item.transactionRequestLog?.reason}</span><br />
					</div>}
					{statusType.includes("Failed") && <div className="request-log__rejection-reason--error-block">
						<span>
							Note: <br />
							{"Blockchain update failed."}
							{isETRIGPNITRV5FeatureEnabled && (
								<span>
									You may proceed by either retry or discard<br /> this attempt via “Retry” or “Discard Request” buttons respectively.
								</span>
							)}
						</span>
						<br />
					</div>}
					<span className="view-request-log-text">by {isSoftDeleteUserEnabled ? renderUserEmailInline(item.email) : item.email} on {moment(item.created).format("DD/MM/YYYY, h:mm A")}</span>
				</div>
			</>
		);
	}

	function renderedUpdatedETRRequestsLegacy({ icon, type, statusType, sharingDataTo, item, changedFields }) {
		const ownerAddress = item.transactionRequestLog?.transferableStoreChangeHistory?.changedDetails?.newOwnerAddress;
		const holderAddress = item.transactionRequestLog?.transferableStoreChangeHistory?.changedDetails?.newHolderAddress;
		let nonChangeableTypes = ['ENDORSE_OWNER', 'SURRENDER'];

		return (
			<>
				<div className="view-request-log-icon">{icon}</div>
				<div className="view-request-log-heading">
					{type} {statusType}<br />
					{sharingDataTo && <><span className="view-request-log-status-text">{sharingDataTo}</span><br /></>}
					{changedFields?.includes("newOwnerOrgId") && <>
						<div className="view-request-log-status-list">
							<CheckListIcon />
							<span className="view-request-log-status-text">Ownership Transfer</span><br />
						</div>
						<div className="request-log__updated-block">
							<Button
								nohover
								nobackground
								addClass={`request-log__updated--header-section ${isOwnerDetailsVisible ? 'expanded' : ''}`}
								submit={() => setIsOwnerDetailsVisible(!isOwnerDetailsVisible)}
								prefix={<div className="request-log__updated--header-section-left">Changes Made</div>}
								suffix={<div className="request-log__updated--header-section-right">
									<div>{isOwnerDetailsVisible ? 'Less Details' : 'More Details'}</div>
									<IconNext />
								</div>}
							/>
							{isOwnerDetailsVisible && (
								<div className="view-request-log-status-list">
									<CheckListIcon />
									<span className="view-request-log-status-text">Assign ownership to: <br />{getOrgName(ownerAddress)} ({ownerAddress})</span><br />
								</div>
							)}
						</div>
					</>}
					{changedFields?.includes("newHolderOrgId") && <>
						<div className="view-request-log-status-list">
							<CheckListIcon />
							<span className="view-request-log-status-text">Holdership Transfer</span><br />
						</div>
						<div className="request-log__updated-block">
							<Button
								nohover
								nobackground
								addClass={`request-log__updated--header-section ${isOwnerDetailsVisible ? 'expanded' : ''}`}
								submit={() => setIsHolderDetailsVisible(!isHolderDetailsVisible)}
								prefix={<div className="request-log__updated--header-section-left">Changes Made</div>}
								suffix={<div className="request-log__updated--header-section-right">
									<div>{isHolderDetailsVisible ? 'Less Details' : 'More Details'}</div>
									<IconNext />
								</div>}
							/>
							{isHolderDetailsVisible && (
								<div className="view-request-log-status-list">
									<CheckListIcon />
									<span className="view-request-log-status-text">Assign holdership to: <br />{getOrgName(holderAddress)} ({holderAddress})</span><br />
								</div>
							)}
						</div>
					</>}
					{isETRIGPNITRV5FeatureEnabled && etrDocDetails?.tokenRegistryVersion === "V5" && <div className="view-request-log-status-list">
						<CheckListIcon />
						<span className="view-request-log-status-text">Remarks: <br />{item?.transferableStore?.remarks?.trim().length > 0 ? item?.transferableStore?.remarks.trim() : "-"}</span><br />
					</div>}
					<div className="view-request-log-status-list">
						<CheckListIcon />
						<span className="view-request-log-status-text">Data Sharing</span><br />
					</div>
					<div className="request-log__updated-block">
						<Button
							nohover
							nobackground
							addClass={`request-log__updated--header-section ${isDataSharingVisible ? 'expanded' : ''}`}
							submit={() => setIsDataSharingVisible(!isDataSharingVisible)}
							prefix={<div className="request-log__updated--header-section-left">Changes Made</div>}
							suffix={<div className="request-log__updated--header-section-right">
								<div>{isDataSharingVisible ? 'Less Details' : 'More Details'}</div>
								<IconNext />
							</div>}
						/>
						{isDataSharingVisible && (
							<div className="view-request-log-status-list">
								<CheckListIcon />
								{nonChangeableTypes.includes(item?.transferableStore?.type) ? <span className="view-request-log-status-text">No Changes <br /></span> :
									<>
										<span className="view-request-log-status-text">Share data to: <br />
											{ownerAddress && <span>{getOrgName(ownerAddress)} <br /></span>}
											{holderAddress && <span>{getOrgName(holderAddress)} <br /></span>}
											{!ownerAddress && !holderAddress && <span>No Changes <br /></span>}
										</span><br />
									</>
								}
							</div>
						)}
					</div>
					<div className="view-request-log-text">by {isSoftDeleteUserEnabled ? renderUserEmailInline(item.email) : item.email} on {moment(item.created).format("DD/MM/YYYY, h:mm A")}</div>
				</div>
			</>
		);
	};

	function renderedUpdatedETRRequests({ idx, icon, type, statusType, sharingDataTo, item, changedFields }) {
		const changeHistory = item.transactionRequestLog?.transferableStoreChangeHistory;
		const changedFieldsArr = changeHistory?.changedFields || [];

		const hasOwner = changedFieldsArr.includes("newOwnerAddress");
		const hasHolder = changedFieldsArr.includes("newHolderAddress");
		const hasRemarks = changedFieldsArr.includes("remarks");

		const ownerAddress = hasOwner ? changeHistory?.changedDetails?.newOwnerAddress : undefined;
		const holderAddress = hasHolder ? changeHistory?.changedDetails?.newHolderAddress : undefined;

		let remarks = undefined;
		if (hasRemarks) {
			if (idx === 0) {
				const storeRemarks = item?.transferableStore?.remarks;
				remarks = (typeof storeRemarks === "string" && storeRemarks.trim().length > 0) ? storeRemarks.trim() : "-";
			} else {
				const changedRemarks = changeHistory?.changedDetails?.remarks;
				remarks = (typeof changedRemarks === "string" && changedRemarks.trim().length > 0) ? changedRemarks.trim() : "-";
			}
		}

		const prevChangeHistory = (activityLog?.[idx + 1]?.transactionRequestLog?.transferableStoreChangeHistory);
		const prevChangedDetails = prevChangeHistory?.changedDetails || {};

		const removedOwner = hasOwner ? prevChangedDetails?.newOwnerAddress : undefined;
		const removedHolder = hasHolder ? prevChangedDetails?.newHolderAddress : undefined;
		let removedRemarks = undefined;
		if (hasRemarks) {
			const prevRemarks = prevChangedDetails?.remarks;
			removedRemarks = (typeof prevRemarks === "string" && prevRemarks.trim().length > 0) ? prevRemarks.trim() : "-";
		};

		return (
			<>
				<div className="view-request-log-icon">{icon}</div>
				<div className="view-request-log-heading">
					{type} {statusType}<br />
					{sharingDataTo && <><span className="view-request-log-status-text">{sharingDataTo}</span><br /></>}
					<div className="view-request-log-status-list">
						<CheckListIcon />
						<span className="view-request-log-status-text">Record Details</span><br />
					</div>
					<ActivityLogAccordion
						added={{
							...(hasOwner ? { owner: ownerAddress } : {}),
							...(hasHolder ? { holder: holderAddress } : {}),
							...(hasRemarks && isETRIGPNITRV5FeatureEnabled && etrDocDetails?.tokenRegistryVersion === "V5" ? { remarks } : {})
						}}
						removed={{
							...(hasOwner ? { owner: removedOwner } : {}),
							...(hasHolder ? { holder: removedHolder } : {}),
							...(hasRemarks && isETRIGPNITRV5FeatureEnabled && etrDocDetails?.tokenRegistryVersion === "V5" ? { remarks: removedRemarks } : {})
						}}
						getOrgName={getOrgName}
						transferType={item?.transferableStore?.type}
					/>
					<div className="view-request-log-text">by {isSoftDeleteUserEnabled ? renderUserEmailInline(item.email) : item.email} on {moment(item.created).format("DD/MM/YYYY, h:mm A")}</div>
				</div>
			</>
		);
	}

	function getTransferType(type) {
		switch (type) {
			case "OWNER_TRANSFER_PENDING_APPROVAL": case "OWNER_TRANSFER_APPROVED": case "OWNER_TRANSFERRING_FAILED": case "OWNER_TRANSFERRED": case "OWNER_TRANSFER_DISCARDED": case "OWNER_TRANSFER_REJECTED": case "OWNER_TRANSFERRING":
				return "Ownership Transfer ";
			case "HOLDER_TRANSFER_PENDING_APPROVAL": case "HOLDER_TRANSFER_APPROVED": case "HOLDER_TRANSFERRING_FAILED": case "HOLDER_TRANSFERRED": case "HOLDER_TRANSFER_DISCARDED": case "HOLDER_TRANSFER_REJECTED": case "HOLDER_TRANSFERRING":
				return "Holdership Transfer ";
			case "OWNER_HOLDER_TRANSFER_PENDING_APPROVAL": case "OWNER_HOLDER_TRANSFER_APPROVED": case "OWNER_HOLDER_TRANSFERRING_FAILED": case "OWNER_HOLDER_TRANSFERRED": case "OWNER_HOLDER_TRANSFER_DISCARDED": case "OWNER_HOLDER_TRANSFER_REJECTED": case "OWNER_HOLDER_TRANSFERRING":
				return "Ownership & Holdership Transfer ";
			case "OWNER_TRANSFER_REJECTION_IN_PROGRESS": case "OWNER_TRANSFER_REJECTION_SUCCESS": case "OWNER_TRANSFER_REJECTION_PENDING_APPROVAL": case "OWNER_TRANSFER_REJECTION_REJECTED": case "OWNER_TRANSFER_REJECTION_APPROVED": case "OWNER_TRANSFER_REJECTION_FAILED": case "OWNER_TRANSFER_REJECTION_DISCARDED":
				return "Ownership Transfer Rejection ";
			case "HOLDER_TRANSFER_REJECTION_IN_PROGRESS": case "HOLDER_TRANSFER_REJECTION_SUCCESS": case "HOLDER_TRANSFER_REJECTION_PENDING_APPROVAL": case "HOLDER_TRANSFER_REJECTION_REJECTED": case "HOLDER_TRANSFER_REJECTION_APPROVED": case "HOLDER_TRANSFER_REJECTION_FAILED": case "HOLDER_TRANSFER_REJECTION_DISCARDED":
				return "Holdership Transfer Rejection ";
			case "OWNER_HOLDER_TRANSFER_REJECTION_PENDING_APPROVAL": case "OWNER_HOLDER_TRANSFER_REJECTION_REJECTED": case "OWNER_HOLDER_TRANSFER_REJECTION_IN_PROGRESS": case "OWNER_HOLDER_TRANSFER_REJECTION_SUCCESS": case "OWNER_HOLDER_TRANSFER_REJECTION_APPROVED": case "OWNER_HOLDER_TRANSFER_REJECTION_FAILED": case "OWNER_HOLDER_TRANSFER_REJECTION_DISCARDED":
				return "Ownership & Holdership Rejection ";
			case "SURRENDER_PENDING_APPROVAL": case "SURRENDER_APPROVED": case "SURRENDERING_FAILED": case "SURRENDERED": case "SURRENDER_DISCARDED": case "SURRENDER_REJECTED": case "SURRENDERING":
				return "Record Surrender ";
			case "NOMINATION_PENDING_APPROVAL": case "NOMINATION_APPROVED": case "NOMINATING_FAILED": case "NOMINATED": case "NOMINATION_DISCARDED": case "NOMINATION_REJECTED": case "NOMINATING":
				return "Ownership Nomination ";
			case "ENDORSEMENT_PENDING_APPROVAL": case "ENDORSEMENT_APPROVED": case "ENDORSEMENT_FAILED": case "ENDORSEMENT_REJECTED":
				return "Owner Transfer";
			case "DATA_ELEMENT_SHARED":
				return "Data Element";
			default:
				return null;
		}
	}

	function getETRStatus(item, idx, owner, holder, sharingDataTo, changedFields, etrName) {
		const { ownerAddress, finalOwnerAddress } = owner;
		const { holderAddress, finalHolderAddress } = holder;
		const updatedEmail = isSoftDeleteUserEnabled ? renderUserEmailInline(item.email) : item.email;
		switch (item.action) {
			case "ISSUING":
				return renderIssuing(updatedEmail, item.created, finalOwnerAddress, finalHolderAddress, item.transferableRecord?.templateName, item?.transferableStore?.remarks);
			case "ISSUED":
				return renderedSuccessful({ etrName, icon: <ActivityLogIconVerified />, email: updatedEmail, created: item.created, sharingDataTo, ETRType: 'Issuance', remarks: item?.transferableStore?.remarks });
			case "ISSUANCE_FAILED":
				return renderFailed({ etrName, email: updatedEmail, created: item.created, sharingDataTo, ETRType: 'Issuance Failed', type: item?.transferableStore?.type });
			case "ISSUANCE_RETRY":
				return renderingStatus({ etrName, email: updatedEmail, created: item.created, ETRType: 'Retrying Issuance', remarks: item?.transferableStore?.remarks, type: item?.transferableStore?.type });
			case "SURRENDERING":
				return renderingStatus({ etrName, email: updatedEmail, created: item.created, ETRType: isETRIGPNITRV5FeatureEnabled ? 'Record Surrender Requested' : 'Surrender Requested', remarks: item?.transferableStore?.remarks, showIssuer: true, type: item?.transferableStore?.type });
			case "SHREDDING":
				return renderingStatus({ etrName, email: updatedEmail, created: item.created, ETRType: isETRIGPNITRV5FeatureEnabled ? 'Record Shredding Requested' : 'Shredding Requested', remarks: item?.transferableStore?.remarks, type: item?.transferableStore?.type });
			case "SHREDDING_FAILED":
				return renderFailed({ etrName, email: updatedEmail, created: item.created, sharingDataTo, ETRType: isETRIGPNITRV5FeatureEnabled ? 'Record Shredding Failed' : 'Shredding Failed', type: item?.transferableStore?.type });
			case "SHREDDED":
				return renderedSuccessful({ etrName, icon: <ActivityLogIconVerified />, email: updatedEmail, created: item.created, ETRType: isETRIGPNITRV5FeatureEnabled ? 'Record Shredding' : 'Shredding', remarks: item?.transferableStore?.remarks });
			case "RESTORED":
				return renderedSuccessful({ etrName, icon: <ActivityLogIconVerified />, email: updatedEmail, created: item.created, ETRType: isETRIGPNITRV5FeatureEnabled ? 'Record Surrender Rejection' : 'Restored', remarks: item?.transferableStore?.remarks });
			case "NOMINATING":
				return renderingStatus({ etrName, email: updatedEmail, created: item.created, ETRType: isETRIGPNITRV5FeatureEnabled ? 'Ownership Nomination Requested' : 'Nominating', remarks: item?.transferableStore?.remarks, type: item?.transferableStore?.type });
			case "RESTORING":
				return renderingStatus({ etrName, email: updatedEmail, created: item.created, ETRType: isETRIGPNITRV5FeatureEnabled ? 'Record Surrender Rejection Requested' : 'Restoring', remarks: item?.transferableStore?.remarks, type: item?.transferableStore?.type });
			case "RESTORING_FAILED":
				return renderFailed({ etrName, email: updatedEmail, created: item.created, sharingDataTo, ETRType: isETRIGPNITRV5FeatureEnabled ? 'Record Surrender Rejection Failed' : 'Restoring Failed', type: item?.transferableStore?.type });
			case "FILE_UPLOAD":
				return renderedSuccessful({ etrName, icon: <CheckDouble />, email: updatedEmail, created: item.created, ETRType: `Record Upload`, remarks: item?.transferableStore?.remarks });
			case "OWNER_TRANSFER_PENDING_APPROVAL": case "HOLDER_TRANSFER_PENDING_APPROVAL": case "OWNER_HOLDER_TRANSFER_PENDING_APPROVAL": case "SURRENDER_PENDING_APPROVAL": case "NOMINATION_PENDING_APPROVAL":
				return renderedTransferredRequests({ icon: <ArrowUpCircle />, type: getTransferType(item.action), statusType: 'Requested', etrName, ownerAddress, holderAddress, item });
			case "OWNER_TRANSFER_APPROVED": case "HOLDER_TRANSFER_APPROVED": case "OWNER_HOLDER_TRANSFER_APPROVED": case "SURRENDER_APPROVED": case "NOMINATION_APPROVED":
				return renderedTransferredRequests({ icon: <CheckDouble />, type: getTransferType(item.action), statusType: 'Approved', etrName, ownerAddress, holderAddress, item });
			case "OWNER_TRANSFERRING_FAILED": case "HOLDER_TRANSFERRING_FAILED": case "OWNER_HOLDER_TRANSFERRING_FAILED": case "SURRENDERING_FAILED": case "NOMINATING_FAILED":
				return renderedTransferredRequests({ icon: <XCircleRed />, type: getTransferType(item.action), statusType: 'Failed', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_TRANSFERRED": case "HOLDER_TRANSFERRED": case "OWNER_HOLDER_TRANSFERRED": case "SURRENDERED": case "NOMINATED":
				return renderedTransferredRequests({ icon: <CheckDouble />, type: getTransferType(item.action), statusType: 'Successful', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_TRANSFER_DISCARDED": case "HOLDER_TRANSFER_DISCARDED": case "OWNER_HOLDER_TRANSFER_DISCARDED": case "SURRENDER_DISCARDED": case "NOMINATION_DISCARDED":
				return renderedTransferredRequests({ icon: <XCircleRed />, type: getTransferType(item.action), statusType: 'Discarded', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_TRANSFER_REJECTED": case "HOLDER_TRANSFER_REJECTED": case "OWNER_HOLDER_TRANSFER_REJECTED": case "SURRENDER_REJECTED": case "NOMINATION_REJECTED":
				return renderedTransferredRequests({ icon: <XCircleRed />, type: getTransferType(item.action), statusType: 'Denied', etrName, ownerAddress, holderAddress, item });
			case "UPDATED":
				return isApprovalScreen ? renderedTransferredRequests({ icon: <ArrowUpCircle />, type: getTransferType(item?.transferableRecord?.eventStatus), statusType: 'Requested', etrName, ownerAddress, holderAddress, item }) : isETRIGPNITRV5FeatureEnabled ? renderedUpdatedETRRequests({ idx, icon: <ArrowUpCircle />, type: getTransferType(item.transferableRecord?.eventStatus), statusType: 'Updated', sharingDataTo: etrName, item, changedFields }) : renderedUpdatedETRRequestsLegacy({ icon: <ArrowUpCircle />, type: getTransferType(item.transferableRecord?.eventStatus), statusType: 'Updated', sharingDataTo: etrName, item, changedFields });
			case "DATA_ELEMENT_SHARED":
				return renderedTransferredRequests({ icon: <CheckDouble />, type: getTransferType(item.action), statusType: 'Sent', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_HOLDER_TRANSFERRING": case "HOLDER_TRANSFERRING": case "OWNER_TRANSFERRING":
				return renderedTransferredRequests({ icon: isETRIGPNITRV5FeatureEnabled ? <ArrowUpCircle /> : <CheckDouble />, type: getTransferType(item.action), statusType: isETRIGPNITRV5FeatureEnabled ? 'Requested' : 'In Progress', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_HOLDER_TRANSFER_REJECTION_SUCCESS": case "HOLDER_TRANSFER_REJECTION_SUCCESS": case "OWNER_TRANSFER_REJECTION_SUCCESS":
				return renderedTransferredRequests({ icon: <CheckDouble />, type: getTransferType(item.action), statusType: 'Successful', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_HOLDER_TRANSFER_REJECTION_IN_PROGRESS": case "HOLDER_TRANSFER_REJECTION_IN_PROGRESS": case "OWNER_TRANSFER_REJECTION_IN_PROGRESS":
				return renderedTransferredRequests({ icon: isETRIGPNITRV5FeatureEnabled ? <ArrowUpCircle /> : <CheckDouble />, type: getTransferType(item.action), statusType: 'Requested', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_HOLDER_TRANSFER_REJECTION_PENDING_APPROVAL": case "HOLDER_TRANSFER_REJECTION_PENDING_APPROVAL": case "OWNER_TRANSFER_REJECTION_PENDING_APPROVAL":
				return renderedTransferredRequests({ icon: isETRIGPNITRV5FeatureEnabled ? <ArrowUpCircle /> : <CheckDouble />, type: getTransferType(item.action), statusType: 'Requested', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_HOLDER_TRANSFER_REJECTION_APPROVED": case "HOLDER_TRANSFER_REJECTION_APPROVED": case "OWNER_TRANSFER_REJECTION_APPROVED":
				return renderedTransferredRequests({ icon: <CheckDouble />, type: getTransferType(item.action), statusType: 'Approved', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_HOLDER_TRANSFER_REJECTION_FAILED": case "HOLDER_TRANSFER_REJECTION_FAILED": case "OWNER_TRANSFER_REJECTION_FAILED":
				return renderedTransferredRequests({ icon: <XCircleRed />, type: getTransferType(item.action), statusType: 'Failed', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_HOLDER_TRANSFER_REJECTION_DISCARDED": case "HOLDER_TRANSFER_REJECTION_DISCARDED": case "OWNER_TRANSFER_REJECTION_DISCARDED":
				return renderedTransferredRequests({ icon: <XCircleRed />, type: getTransferType(item.action), statusType: 'Discarded', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			case "OWNER_HOLDER_TRANSFER_REJECTION_REJECTED": case "HOLDER_TRANSFER_REJECTION_REJECTED": case "OWNER_TRANSFER_REJECTION_REJECTED":
				return renderedTransferredRequests({ icon: <XCircleRed />, type: getTransferType(item.action), statusType: 'Denied', etrName, ownerAddress: finalOwnerAddress, holderAddress: finalHolderAddress, item });
			default:
				return null;
		}
	}

	function getSharedToOrgName(transferableStore) {
		const sharedToOrgId = [...new Set([transferableStore?.newOwnerOrgId, transferableStore?.newHolderOrgId].filter(Boolean))];
		const sharedToOrgName = sharedToOrgId.map((orgId) => {
			return staticData?.orgs?.find(org => org.orgId === orgId)?.name
		});
		return sharedToOrgName.filter(Boolean).join(', ') || null;
	}

	const renderETRBody = activityLog?.map((item, idx) => {
		const ownerAddress = item.transactionRequestLog?.transferableStoreChangeHistory?.changedDetails?.newOwnerAddress;
		const holderAddress = item.transactionRequestLog?.transferableStoreChangeHistory?.changedDetails?.newHolderAddress;
		const finalOwnerAddress = item.transferableStore?.newOwnerAddress;
		const finalHolderAddress = item.transferableStore?.newHolderAddress;

		const sharingDataTo = getSharedToOrgName(item.transferableStore);
		const etrName = item.transferableRecord?.name || null;
		const changedFields = item.transactionRequestLog?.transferableStoreChangeHistory?.changedFields;
		return (
			<div className="view-request-log" key={item?.email}>
				{getETRStatus(item, idx, { ownerAddress, finalOwnerAddress }, { holderAddress, finalHolderAddress }, sharingDataTo, changedFields, etrName)}
			</div>
		)
	});

	return (
		<Modal visible={visible} data-testid="view-request-log-modal" variant="medium" close={close}>
			<CardConfigure>
				<CardConfigure.Header>
					<CardConfigure.Header.Title>
						<div className="card-configure__header__left">
							<div className="card-configure__title">
								<div className="card-configure__title__left">
									<span className="issue-document-modal__icon">
										<ETRActivityLogIcon />
									</span>
								</div>
								<div className="card-configure__title__right">
									<div className="card-configure__title__right__top">
										Activity Log
									</div>
								</div>
							</div>
						</div>
					</CardConfigure.Header.Title>
				</CardConfigure.Header>
				<CardConfigure.Body>
					<CardConfigure.Body.Col>
						<div className="view-request-log-modal-body">
							{activityLogType !== "etr" && (
								activityLog?.length > 0 ? renderBody : <div>No Data Found</div>
							)}
							{activityLogType === "etr" && (
								activityLog?.length > 0 ? renderETRBody : <div>No Data Found</div>
							)}
						</div>
					</CardConfigure.Body.Col>
				</CardConfigure.Body>
				<CardConfigure.Footer>
					<CardConfigure.Footer.Right>
						<Button
							text="Close"
							round
							full
							data-testid="view-req-log-close-btn"
							submit={close}
						/>
					</CardConfigure.Footer.Right>
				</CardConfigure.Footer>
			</CardConfigure>
		</Modal>
	);
};

ViewActivityLogModal.propTypes = {
	visible: PropTypes.bool,
	close: PropTypes.func,
	schema: PropTypes.object,
	activityLog: PropTypes.array,
	activityLogType: PropTypes.string,
	isApprovalScreen: PropTypes.bool,
	etrDocDetails: PropTypes.object
};

export default ViewActivityLogModal;
